/**
 * Request Attachments Section Component
 *
 * @module @features/requests/components/RequestDetails/components/RequestAttachments
 */

import React from 'react';
import { Button } from '@dynamon-as/ui';
import { useI18n } from '@shared/i18n';
import type { Request, Attachment } from '@features/requests/types';
import styles from '../RequestDetails.module.css';

/**
 * Formats file size in human-readable format
 */
function formatFileSize(
    bytes: number,
    t: (key: string, params?: Record<string, string | number>) => string,
): string {
    if (bytes < 1024) {
        return t('requests.fileSize.bytes', { size: bytes });
    }
    if (bytes < 1024 * 1024) {
        return t('requests.fileSize.kb', {
            size: (bytes / 1024).toFixed(2),
        });
    }
    return t('requests.fileSize.mb', {
        size: (bytes / (1024 * 1024)).toFixed(2),
    });
}

/**
 * Props for RequestAttachments component
 */
export interface RequestAttachmentsProps {
    request: Request;
}

/**
 * Displays request attachments
 */
export function RequestAttachments({
    request,
}: RequestAttachmentsProps): React.ReactElement | null {
    const { t } = useI18n();

    if (!request.attachments || request.attachments.length === 0) return null;

    return (
        <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
                {t('requests.details.attachments')}
            </h3>
            <ul className={styles.attachments}>
                {request.attachments.map((attachment: Attachment) => (
                    <li
                        key={attachment.id}
                        className={styles.attachment}
                    >
                        <span className={styles.attachmentName}>
                            {attachment.name}
                        </span>
                        <span className={styles.attachmentSize}>
                            ({formatFileSize(attachment.size, t)})
                        </span>
                        {attachment.url && (
                            <Button
                                text={t('requests.details.download')}
                                color="primary"
                                size="small"
                                onClick={(): void => {
                                    void window.open(attachment.url, '_blank');
                                }}
                            />
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
